import {
  getExtraLastMileInfo,
  getLineItemsOfSelectedOrder,
  getOrderId,
  getOrderingSystem,
  getSelectedLineItems,
  getSelectedOrder,
  getSelectedSubscriptionList,
  getSelectedRbiItemsForLastMileRefund,
} from '../../selectors/tools';
import {
  extractNonHistoricalTicketIdFromState,
  getCancelAndRefundItems,
  getRefundAmountDetails,
  getRefundOnlyItems,
  getSimplrAccountIdOfSelectedTicket,
} from 'extractFromState';
import { shopifyPostPutQuery } from '../../api/orders/shopify';
import {
  lastMileRefundAmountType,
  lastMileSupportedOrderingSystems,
  lastMileValidItemsType,
} from '../../constants/lastMile';
import { shopifyGetOrderShippingRefundAmount } from 'actions/tools/ShopifyActions';
import { showErrorPopup } from 'components/popups/ErrorPopup/ErrorPopup';
import * as ordersActions from 'actions/tools/OrdersSearchActions';
import { getMappedValueFromOrderOrItem } from '../../util';
import { addExtraLastMileInfo, clearExtraLastMileInfo } from '../../actions/tools/OrdersSearchActions';
import { showError } from '../../util/errorPopupUtil';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { setLoading } from '../index';

const getSelectedItems = () => async (dispatch, getState) => {
  const state = getState();
  const orderingSystem = getOrderingSystem(state);
  switch (orderingSystem) {
    case lastMileSupportedOrderingSystems.SHOPIFY:
    case lastMileSupportedOrderingSystems.COMMON:
      return getSelectedLineItems(state);
    case lastMileSupportedOrderingSystems.CUSTOM_RBI:
      // RBI items are already in state after nodes SELECT_ORDER_RBI_FULL_REFUND and SELECT_ORDER_RBI_PARTIAL_REFUND.
      // After node SELECT_ORDER_RBI_FULL_REFUND it will get all items(and there is no way to select items for full refund flow).
      // After node SELECT_ORDER_RBI_PARTIAL_REFUND it will get selectedItemsAndSubItemsForRefund with data
      // and will also have other items but with zero quantity so we are filtering them.
      return getSelectedRbiItemsForLastMileRefund(state);
    default:
      throw new Error(`getSelectedItems: Ordering system ${orderingSystem} is not supported!`);
  }
};

const getRefundApprovedItems = () => async (dispatch, getState) => {
  const state = getState();
  const orderingSystem = getOrderingSystem(state);
  switch (orderingSystem) {
    case lastMileSupportedOrderingSystems.SHOPIFY:
      const refundOnlyItems = getRefundOnlyItems(state);
      const cancelAndRefundItems = getCancelAndRefundItems(state);

      return [...refundOnlyItems, ...cancelAndRefundItems];
    case lastMileSupportedOrderingSystems.CUSTOM_RBI:
      // RBI items are already in state after nodes SELECT_ORDER_RBI_FULL_REFUND and SELECT_ORDER_RBI_PARTIAL_REFUND.
      // After node SELECT_ORDER_RBI_FULL_REFUND it will get all items(and there is no way to select items for full refund flow).
      // After node SELECT_ORDER_RBI_PARTIAL_REFUND it will get selectedItemsAndSubItemsForRefund with data
      // and will also have other items but with zero quantity so we are filtering them.
      return getSelectedRbiItemsForLastMileRefund(state);
    default:
      throw new Error(`getRefundApprovedItems: Ordering system ${orderingSystem} is not supported!`);
  }
};

const getItemsForLastMile = (validItemsType) => async (dispatch, getState) => {
  const state = getState();
  switch (validItemsType) {
    case lastMileValidItemsType.ALL_ITEMS:
      return getLineItemsOfSelectedOrder(state);
    case lastMileValidItemsType.SELECTED_ITEMS:
      return await dispatch(getSelectedItems());
    case lastMileValidItemsType.REFUND_APPROVED_ITEMS:
      return await dispatch(getRefundApprovedItems());
    default:
      throw new Error(`getItemsForLastMile: Valid items type ${validItemsType} is not supported.`);
  }
};

const getOrderShipping = () => async (dispatch, getState) => {
  const state = getState();
  const orderingSystem = getOrderingSystem(state);
  const selectedOrder = getSelectedOrder(state);
  const orderId = getOrderId(selectedOrder, orderingSystem);
  switch (orderingSystem) {
    case lastMileSupportedOrderingSystems.SHOPIFY:
      const options = {
        shopName: selectedOrder.shopName,
        orderId,
      };

      return await dispatch(shopifyGetOrderShippingRefundAmount(options));
    default:
      throw new Error(`getOrderShipping: Ordering system ${orderingSystem} is not supported!`);
  }
};

const getOrderRefundAmount = () => async (dispatch, getState) => {
  const state = getState();
  const orderingSystem = getOrderingSystem(state);
  const selectedOrder = getSelectedOrder(state);
  const orderId = getOrderId(selectedOrder, orderingSystem);
  switch (orderingSystem) {
    case lastMileSupportedOrderingSystems.SHOPIFY:
      const { total } = await dispatch(ordersActions._getRefundForCancelRefundFlow(orderId));

      return total;
    case lastMileSupportedOrderingSystems.CUSTOM_RBI:
      // For RBI full or partial refund amount is already in state
      // after nodes SELECT_ORDER_RBI_FULL_REFUND and SELECT_ORDER_RBI_PARTIAL_REFUND.
      const refundAmountDetails = getRefundAmountDetails(state);

      return refundAmountDetails ? refundAmountDetails.totalCents : null;
    default:
      throw new Error(`getOrderRefundAmount: Ordering system ${orderingSystem} is not supported!`);
  }
};

const getRefundAmountForLastMile = (refundAmountType) => async (dispatch, getState) => {
  switch (refundAmountType) {
    case lastMileRefundAmountType.NO_REFUND_DATA_NEEDED:
      return null;
    case lastMileRefundAmountType.SHIPPING_ON_FULL_ORDER:
      return await dispatch(getOrderShipping());
    case lastMileRefundAmountType.VALID_ITEMS_FULL_REFUND:
      return await dispatch(getOrderRefundAmount());
    default:
      throw new Error(`getRefundAmountForLastMile: Refund amount type "${refundAmountType}" is not supported!`);
  }
};

const getShopifyItemFulfillment = (selectedOrder, item, orderingSystem) => {
  const fulfillments = get(selectedOrder, 'fulfillments', []);

  return fulfillments.find((fulfillment) => {
    const lineItems = fulfillment.line_items || fulfillment.shipmentItems || [];

    return lineItems.some(
      (lineItem) =>
        getMappedValueFromOrderOrItem(item, 'itemId', orderingSystem) ===
        getMappedValueFromOrderOrItem(lineItem, 'itemId', orderingSystem)
    );
  });
};

const _getOrderDataInformation =
  (selectedOrder, orderData, field, orderingSystem, lastMileSettings) => async (dispatch) => {
    if (field === 'totalPriceOfItems') {
      const { validItemsType = null } = lastMileSettings;
      const items = await dispatch(getItemsForLastMile(validItemsType));

      return getMappedValueFromOrderOrItem(items, orderData[field], orderingSystem);
    }

    return getMappedValueFromOrderOrItem(selectedOrder, orderData[field], orderingSystem);
  };

const _getSubscriptionDataInformation = (selectedSubscription, subscriptionData, orderingSystem) => {
  const subscriptionFields = {};
  for (const field in subscriptionData) {
    subscriptionFields[field] = getMappedValueFromOrderOrItem(
      selectedSubscription,
      subscriptionData[field],
      orderingSystem
    );
  }

  return subscriptionFields;
};

export const storeLastMileData =
  (simplrAccountId, conversationId, reason, lastMileSettings) => async (dispatch, getState) => {
    try {
      dispatch(setLoading(true, 'reasonContainer'));
      const state = getState();
      const orderingSystem = getOrderingSystem(state);
      if (!Object.values(lastMileSupportedOrderingSystems).includes(orderingSystem)) {
        return showErrorPopup(`Ordering system ${orderingSystem} is not supported!`, 'Error');
      }

      const { orderData = {}, subscriptionData = {}, refundAmountType = null } = lastMileSettings;
      const extraLastMileInfo = {
        reason,
        items: {},
        subscriptions: {},
      };

      if (!isEmpty(orderData)) {
        const selectedOrder = getSelectedOrder(state);
        if (isEmpty(selectedOrder)) {
          return showErrorPopup('Unable to grab order data as no order is selected.', 'Error');
        }

        const refundAmount = await dispatch(getRefundAmountForLastMile(refundAmountType));
        if (refundAmount) {
          extraLastMileInfo[refundAmountType] = refundAmount;
        }

        for (const field in orderData) {
          extraLastMileInfo[field] = await dispatch(
            _getOrderDataInformation(selectedOrder, orderData, field, orderingSystem, lastMileSettings)
          );
        }

        extraLastMileInfo.items = await dispatch(getExtraLastMileInfoItems(lastMileSettings));
      }

      if (!isEmpty(subscriptionData)) {
        const selectedSubscriptionList = getSelectedSubscriptionList(state);
        if (isEmpty(selectedSubscriptionList)) {
          return showErrorPopup('Unable to grab subscription data as no subscription is selected.', 'Error');
        }

        for (const selectedSubscription of selectedSubscriptionList) {
          extraLastMileInfo.subscriptions[selectedSubscription.id] = _getSubscriptionDataInformation(
            selectedSubscription,
            subscriptionData,
            orderingSystem
          );
        }
      }

      dispatch(addExtraLastMileInfo(extraLastMileInfo));
    } catch (error) {
      showError(error);
    } finally {
      dispatch(setLoading(false, 'reasonContainer'));
    }
  };

const getExtraLastMileInfoItems = (lastMileSettings) => async (dispatch, getState) => {
  const { itemData = {} } = lastMileSettings;
  if (!isEmpty(itemData)) {
    const state = getState();
    const selectedOrder = getSelectedOrder(state);
    const orderingSystem = getOrderingSystem(state);
    const { validItemsType = null } = lastMileSettings;
    const items = await dispatch(getItemsForLastMile(validItemsType));
    let extraLastMileInfoItems = {};
    items.forEach((_item) => {
      const itemTempData = {};
      const item = cloneDeep(_item);
      // adding Shopify fulfillment data for getCarrier and getTrackingNumber to work properly
      if (orderingSystem === lastMileSupportedOrderingSystems.SHOPIFY) {
        item.fulfillmentData = getShopifyItemFulfillment(selectedOrder, item, orderingSystem);
      }
      Object.keys(itemData).forEach((key) => {
        itemTempData[key] = getMappedValueFromOrderOrItem(item, itemData[key], orderingSystem);
      });
      extraLastMileInfoItems[getMappedValueFromOrderOrItem(item, 'itemId', orderingSystem)] = itemTempData;
    });

    return extraLastMileInfoItems;
  }

  return {};
};

export const createLastMileRecord =
  (extraParams = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const extraLastMileInfo = getExtraLastMileInfo(state);
    const simplrAccountId = getSimplrAccountIdOfSelectedTicket(state);
    const conversationId = extractNonHistoricalTicketIdFromState(state);
    const orderingSystem = getOrderingSystem(state);
    if (!isEmpty(extraLastMileInfo) && Object.values(lastMileSupportedOrderingSystems).includes(orderingSystem)) {
      const payload = {
        simplrAccountId,
        conversationId,
        extraLastMileInfo: {
          ...extraLastMileInfo,
          ...extraParams,
        },
      };

      try {
        // currently endpoint /createLastMileRefund is under Shopify backend section but works for any integration
        // TODO: move endpoint /createLastMileRefund out of Shopify backend section
        await shopifyPostPutQuery('post', '/createLastMileRefund', payload);
        dispatch(clearExtraLastMileInfo());
      } catch (e) {
        showError(e);
      }
    }
  };
